import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import copyJson from '../../demo/fixtures/w3/copy.json'
import rulesJson from '../../demo/fixtures/w3/rules.json'
import rulesW4Json from '../../demo/fixtures/w4/rules.json'
import { selectAutopilot, selectAutopilotEligible, selectAutopilotScope, selectGenerated, selectOptimizations, selectUplift } from '../../demo/selectors'
import { useDemoStore } from '../../demo/store'
import type { AutopilotGoal, AutopilotState, AutopilotTabDef, Campaign, PlanReason } from '../../demo/types'
import { Card } from '../../ui'
import { AutopilotDialog } from './AutopilotDialog'
import { AutopilotHead } from './AutopilotHead'
import { AutopilotInvite } from './AutopilotInvite'
import type { AnalysisTargets } from './AutopilotLog'
import { AutopilotTabs, type EngineTabKey } from './AutopilotTabs'
import { formatTargetInput, needsHumanDestination } from './autopilot'
import { GeneratedPane } from './panes/GeneratedPane'
import { LogPane } from './panes/LogPane'
import { ReasoningPane } from './panes/ReasoningPane'
import { ScopePane } from './panes/ScopePane'
import { SettingsPane } from './panes/SettingsPane'
import styles from './AutopilotSection.module.css'

interface CopyFixture {
  invite: { title: string; body: string[]; cta: string }
  dialog: { title: string; sub: string; goalLabel: string; cancel: string; ok: string }
  logTitle: string
  running: string
  onToast: string
  offToast: string
  noFinding: string
  noData: string
  undoToast: string
}
interface RulesFixture { runMs: number; itemFadeMs: number }
interface Bound { key: string; on: boolean }
interface RulesW4Fixture { tabs: AutopilotTabDef[]; bounds: Bound[] }
interface SettingsDraft { goal: AutopilotGoal; targetText: string; bounds: Record<string, boolean> }

const copy = copyJson as unknown as CopyFixture
const rules = rulesJson as unknown as RulesFixture
const rulesW4 = rulesW4Json as unknown as RulesW4Fixture
const defaultTab = rulesW4.tabs.find((item) => item.default)?.key ?? 'log'
const defaultBounds = Object.fromEntries(rulesW4.bounds.map((item) => [item.key, item.on]))

function makeSettingsDraft(autopilot: AutopilotState): SettingsDraft {
  return { goal: autopilot.goal, targetText: formatTargetInput(autopilot.target), bounds: { ...defaultBounds } }
}

interface AutopilotSectionProps {
  campaign: Campaign
  targets: AnalysisTargets
  audienceEdited: boolean
  onOpenSettings: () => void
  onOpenLink: () => void
  onToast: (message: string) => void
}

export function AutopilotSection({ campaign, targets, audienceEdited, onOpenSettings, onOpenLink, onToast }: AutopilotSectionProps) {
  const autopilotByCampaign = useDemoStore((state) => state.autopilotByCampaign)
  const campaigns = useDemoStore((state) => state.campaigns)
  const optimizationState = useDemoStore((state) => state.optimizations)
  const selectedByCampaign = useDemoStore((state) => state.selectedByCampaign)
  const turnOnAutopilot = useDemoStore((state) => state.turnOnAutopilot)
  const turnOffAutopilot = useDemoStore((state) => state.turnOffAutopilot)
  const updateAutopilotGoal = useDemoStore((state) => state.updateAutopilotGoal)
  const runCheck = useDemoStore((state) => state.runCheck)
  const undoOptimization = useDemoStore((state) => state.undoOptimization)
  const autopilot = useMemo(() => { void autopilotByCampaign; return selectAutopilot(campaign.id) }, [autopilotByCampaign, campaign.id])
  const eligible = useMemo(() => { void campaigns; return selectAutopilotEligible(campaign.id) }, [campaign.id, campaigns])
  const optimizations = useMemo(() => { void optimizationState; return selectOptimizations(campaign.id) }, [campaign.id, optimizationState])
  const uplift = useMemo(() => selectUplift(campaign.id), [campaign.id])
  const scope = useMemo(() => { void autopilotByCampaign; void optimizationState; return selectAutopilotScope(campaign.id) }, [autopilotByCampaign, campaign.id, optimizationState])
  // AI 素材树的「已采用」= 当前在投素材 ∩ 树上已入库的 AI 素材，故必须跟着 selectedByCampaign 重算
  const generated = useMemo(() => { void selectedByCampaign; return selectGenerated(campaign.id) }, [campaign.id, selectedByCampaign])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<EngineTabKey>(defaultTab)
  const [running, setRunning] = useState(false)
  const [notice, setNotice] = useState<PlanReason | null>(null)
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null)
  const [animateIds, setAnimateIds] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState(false)
  const [scopeEnabledByCampaign, setScopeEnabledByCampaign] = useState<Record<string, Record<string, boolean>>>({})
  const [settingsDraftByCampaign, setSettingsDraftByCampaign] = useState<Record<string, SettingsDraft>>({})
  const timers = useRef(new Set<number>())
  const sectionRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const schedule = useCallback((callback: () => void, delay: number) => {
    const timer = window.setTimeout(() => { timers.current.delete(timer); callback() }, delay)
    timers.current.add(timer)
  }, [])

  useEffect(() => () => {
    for (const timer of timers.current) window.clearTimeout(timer)
    timers.current.clear()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('focus') !== 'autopilot') return
    params.delete('focus')
    window.requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
    void navigate(`${location.pathname}${params.size ? `?${params}` : ''}`, { replace: true, state: null })
  }, [location.pathname, location.search, navigate])

  const run = useCallback((origin: 'turn-on' | 'check') => {
    if (running) return
    const before = new Set(useDemoStore.getState().optimizations.map((item) => item.id))
    setNotice(null)
    setAnimateIds(new Set())
    setVisibleIds(null)
    setActiveTab('log')
    setRunning(true)
    schedule(() => {
      const result = runCheck(campaign.id)
      const current = selectOptimizations(campaign.id)
      const added = current.filter((item) => !before.has(item.id)).map((item) => item.id)
      const baseVisible = new Set(current.filter((item) => before.has(item.id)).map((item) => item.id))
      setRunning(false)
      setNotice(result.reason)
      if (added.length === 0) {
        setVisibleIds(null)
        return
      }
      setExpanded(true)
      setVisibleIds(baseVisible)
      added.forEach((id, index) => schedule(() => {
        setVisibleIds((previous) => new Set([...(previous ?? []), id]))
        setAnimateIds((previous) => new Set([...previous, id]))
        if (index === added.length - 1 && result.reason === 'ok') {
          onToast(origin === 'turn-on' ? copy.onToast.replace('{n}', String(result.count)) : `这一轮检查完了，调整了 ${result.count} 项`)
        }
      }, index * rules.itemFadeMs))
    }, rules.runMs)
  }, [campaign.id, onToast, runCheck, running, schedule])

  const confirm = (input: { goal: AutopilotGoal; target: number }) => {
    setDialogOpen(false)
    turnOnAutopilot(campaign.id, input)
    run('turn-on')
  }
  const toggle = () => {
    if (!autopilot?.on) {
      setDialogOpen(true)
      return
    }
    turnOffAutopilot(campaign.id)
    onToast(copy.offToast)
  }
  const undo = (id: string) => {
    const item = useDemoStore.getState().optimizations.find((candidate) => candidate.id === id)
    if (!item || item.undone || !item.payload) return
    undoOptimization(id)
    onToast(copy.undoToast)
  }
  const go = (target: string) => {
    if (needsHumanDestination(target) === 'link') onOpenLink()
    else onOpenSettings()
  }

  let activePanel: ReactNode = null
  if (autopilot) {
    const scopeDefaults = Object.fromEntries(scope.map((item) => [item.key, true]))
    const scopeEnabled = scopeEnabledByCampaign[campaign.id] ?? scopeDefaults
    const settingsDraft = settingsDraftByCampaign[campaign.id] ?? makeSettingsDraft(autopilot)
    const updateSettingsDraft = (patch: Partial<SettingsDraft>) => setSettingsDraftByCampaign((previous) => ({
      ...previous,
      [campaign.id]: { ...(previous[campaign.id] ?? makeSettingsDraft(autopilot)), ...patch },
    }))
    const panels: Record<EngineTabKey, ReactNode> = {
      log: <LogPane running={running} notice={notice} optimizations={optimizations} visibleIds={visibleIds} animateIds={animateIds} expanded={expanded} audienceEdited={audienceEdited} targets={targets} onExpandedChange={setExpanded} onUndo={undo} onGo={go} />,
      reasoning: <ReasoningPane items={optimizations} />,
      scope: <ScopePane items={scope} enabled={scopeEnabled} onEnabledChange={(key, enabled) => setScopeEnabledByCampaign((previous) => ({ ...previous, [campaign.id]: { ...(previous[campaign.id] ?? scopeDefaults), [key]: enabled } }))} onToast={onToast} />,
      creatives: <GeneratedPane campaignId={campaign.id} productId={campaign.productId} generated={generated} onToast={onToast} />,
      settings: <SettingsPane goal={settingsDraft.goal} targetText={settingsDraft.targetText} bounds={settingsDraft.bounds} onGoalChange={(goal) => updateSettingsDraft({ goal })} onTargetTextChange={(targetText) => updateSettingsDraft({ targetText })} onBoundChange={(key, enabled) => updateSettingsDraft({ bounds: { ...settingsDraft.bounds, [key]: enabled } })} onSave={(input) => { updateAutopilotGoal(campaign.id, input); onToast('已保存，下次检查起生效') }} onTurnOff={() => { turnOffAutopilot(campaign.id); onToast(copy.offToast) }} />,
    }
    activePanel = panels[activeTab]
  }

  return (
    <div ref={sectionRef}>
      {autopilot === null ? <AutopilotInvite copy={copy.invite} eligible={eligible} onOpen={() => setDialogOpen(true)} /> : (
        <Card className={`${styles.apCard} ${autopilot.on ? '' : styles.apCardOff}`} data-scope="ap-card">
          <AutopilotHead autopilot={autopilot} resultMetric={campaign.resultMetric} optimizationCount={optimizations.length} uplift={uplift} running={running} onRun={() => run('check')} onToggle={toggle} />
          <AutopilotTabs items={rulesW4.tabs} value={activeTab} counts={{ optimizations: optimizations.length, generated: generated.total }} onChange={setActiveTab} />
          <div aria-labelledby={`ap-tab-${activeTab}`} className={styles.tabBody} id={`ap-panel-${activeTab}`} role="tabpanel">{activePanel}</div>
        </Card>
      )}
      {dialogOpen ? <AutopilotDialog open campaign={campaign} autopilot={autopilot} copy={copy.dialog} onClose={() => setDialogOpen(false)} onConfirm={confirm} /> : null}
    </div>
  )
}
